/**
* Snackbar component can be used to display a toast message on success or failure of an action
*
* @module views/components/SnackBar
* @memberof -Common
*/
import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { SnackBar as PalSnackBar } from 'web-palette/dist/components/SnackBar/SnackBar';

import Button from '@ulta/core/components/Button/Button';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';

import * as utils from './SnackBar';
import animationVariables from './SnackBar.scss';

const { SNACKBAR_DISPLAY_DURATION } = constants;

const AnimationSlideIn = 'SlideIn';
const AnimationFadeOut = 'FadeOut';

/**
  * Represents a SnackBar component
  *
  * @method
  * @param {SnackBarProps} props - React properties passed from composition
  * @returns SnackBar
  */
export const SnackBar = function( props ){
  const animationTimer = useRef();
  const {
    action,
    bgColor = defaultProps.bgColor,
    borderColor = defaultProps.borderColor,
    className,
    handleAction,
    icon,
    message = defaultProps.message,
    onClose,
    snackBarId,
    textColor,
    iconColor,
    style,
    thumbnailImage
  } = props;
  const [animationClass, setAnimationClass] = useState( AnimationSlideIn );

  useEffect( () => {
    return ( () => {
      clearTimeout( animationTimer.current );
    } );
  }, [] );

  useEffect( () => {
    handleSetTimeout( { animationTimer, message }, { setAnimationClass, onClose } );
  }, [snackBarId] );

  if( !message ){
    return null;
  }

  const externalActionElementWrapper = ( action?.label ? (
    <div className='SnackBar__ActionWrapper'>
      <Button compact
        action={ action }
        label={ action.label }
        onClick={ handleActionClick( { handleAction } ) }
        tertiary
        tiny
      />
    </div>
  ) : null );

  const formattedThumbnail = thumbnailImage ? getUltaAssetHtml( { thumbnailImage: thumbnailImage } ) : null;

  let formattedStyle = style;
  if( !style ){
    formattedStyle = thumbnailImage ? 'thumbnail' : 'unstyled';
  }

  let formattedBackgroundColor = paletteBackgroundColorMapping[bgColor];
  if( !formattedBackgroundColor ){
    formattedBackgroundColor = bgColor;
  }

  let formattedBorderColor = paletteBorderColorMapping[borderColor];
  if( !formattedBorderColor ){
    formattedBorderColor = borderColor;
  }

  let formattedIconColor = iconColor;
  if( !iconColor ){
    formattedIconColor = paletteIconColorMapping[borderColor];
  }

  return (
    <div
      className={ classNames( 'SnackBar', {
        [className]: className
      } ) }
      key={ snackBarId }
    >
      <PalSnackBar
        title={ message }
        iconName={ icon }
        textColor={ textColor }
        snackBarId={ snackBarId }
        { ...spreadProps( {
          externalActionElement: externalActionElementWrapper,
          backgroundColor:formattedBackgroundColor,
          borderColor:formattedBorderColor,
          iconColor:formattedIconColor,
          style:formattedStyle,
          externalMediaElement:formattedThumbnail,
          animationClassName:animationClass
        } ) }
      />
    </div>
  );
};

/**
* Defines the palette background color based on the core background colors
*/
export const paletteBackgroundColorMapping = {
  'notification-100' : 'background-notification',
  'notification-200' : 'background-notification-strong',
  'notification-300' : 'background-notification-strong',
  'notification-400' : 'background-notification-xstrong',
  'notification-600' : 'background-notification-xstrong',
  'error-100' : 'background-error',
  'error-200' : 'background-error-strong',
  'error-300' : 'background-error-strong',
  'error-400' : 'background-error-xstrong',
  'error-600' : 'background-error-xstrong'
};

/**
* Defines the palette border color based on the core border colors
*/
export const paletteBorderColorMapping = {
  'notification-100' : 'border-notification-subtle',
  'notification-200' : 'border-notification-subtle',
  'notification-300' : 'border-notification-subtle',
  'notification-400' : 'border-notification',
  'notification-600' : 'border-notification',
  'error-100' : 'border-error',
  'error-200' : 'border-error',
  'error-300' : 'border-error',
  'error-400' : 'border-error',
  'error-600' : 'border-error'
};

/**
* Defines the palette icon color based on the border color
*/
export const paletteIconColorMapping = {
  'notification-100' : 'notification',
  'notification-200' : 'notification',
  'notification-300' : 'notification',
  'notification-400' : 'notification',
  'notification-600' : 'notification',
  'error-100' : 'error',
  'error-200' : 'error',
  'error-300' : 'error',
  'error-400' : 'error',
  'error-600' : 'error',
  default: 'default'
};

/**
* set timeout handler
* @method
* @param {Object} methods - Passing methods as an argument for updating the state
*/
export const handleSetTimeout = ( data, methods ) => {
  const { animationTimer, message } = data;
  const SnackbarTotalDisplayDuration = utils.getSnackbarAnimationValues( 'SnackbarTotalDisplayDuration' );
  if( !message ){
    return;
  }
  const { setAnimationClass, onClose } = methods;
  if( animationTimer.current ){
    clearTimeout( animationTimer.current );
  }
  animationTimer.current = setTimeout( () => {
    setAnimationClass( AnimationFadeOut );
    onClose && onClose();
  }, SnackbarTotalDisplayDuration );
};

/**
* action Button handler function
* @method
* @param {Object} methods - Passing methods as an argument for updating the state
*/
export const handleActionClick = ( methods ) => {
  methods.handleAction && methods.handleAction();
};

/**
* helper function used to convert ms duration values to number
* @method
* @param {Object} animationDuration - animation duration in ms
*/
export const convertAnimationDurationToNumber = ( animationDuration ) => {
  const formattedAnimationDuration = animationDuration?.replace( 'ms', '' );
  const duration = formattedAnimationDuration && Number( formattedAnimationDuration );
  return duration || null;
};

/**
* helper function used to get snackBar animation values
* @method
* @param {Object} animationKey - Passing animationKey - oneOf( 'SnackbarEntryDuration', 'SnackbarExitDuration', 'SnackbarDisplayDuration', 'SnackbarTotalDisplayDuration' )
*/
export const getSnackbarAnimationValues = ( animationKey ) => {
  // using animationVariables to access timing values based on animation animationVariables from 'web-styles/src/_animation.scss'

  switch ( animationKey ){
    case 'SnackbarEntryDuration':
      return utils.convertAnimationDurationToNumber( animationVariables.animationentryduration );
    case 'SnackbarExitDuration':
      return utils.convertAnimationDurationToNumber( animationVariables.animationexitduration );
    case 'SnackbarDisplayDuration':
      return SNACKBAR_DISPLAY_DURATION;
    case 'SnackbarTotalDisplayDuration':
      const SnackbarEntryDuration = utils.convertAnimationDurationToNumber( animationVariables.animationentryduration );
      const SnackbarDisplayDuration = SNACKBAR_DISPLAY_DURATION;
      const SnackbarTotalDisplayDuration = SnackbarDisplayDuration + SnackbarEntryDuration;
      return SnackbarTotalDisplayDuration;
    default:
      return null;
  }
};


/**
  * Property type definitions
  * @typedef SnackBarProps
  * @type {object}
  * @property {object} action - Button action - TBD
  * @property {string} type - type of the component
  * @property {string} className - className to customize style
  * @property {number} displayDuration - Specify the duration in which it's displayed
  * @property {string} icon - Icon name
  * @property {string} message - Message text
  * @property {string} textColor - Text Color
  * @property {string} iconColor - Icon Color
  * @property {string} style - Style as ( default / unstyled / thumbnail / error )
  * @property {string} thumbnailImage - Thumbnail Image
  */
export const propTypes =  {
  action: PropTypes.object, // TBD
  bgColor: PropTypes.string,
  borderColor: PropTypes.string,
  className: PropTypes.string,
  displayDuration: PropTypes.number,
  icon: PropTypes.string,
  message: PropTypes.string.isRequired,
  textColor: PropTypes.string,
  iconColor: PropTypes.string,
  style: PropTypes.string,
  thumbnailImage: PropTypes.object
};

/**
  * Default values for passed properties
  *
  * @type {object}
  * @property {string} type='success' - type is success by default
  */
export const defaultProps =  {
  message: '',
  bgColor: 'notification-100',
  borderColor: 'notification-600'
};

SnackBar.propTypes = propTypes;
SnackBar.defaultProps = defaultProps;

export default SnackBar;
